import {useMemo} from 'react';

import {
  KVLoadEvent,
  useKVCacheManager,
} from '../../../../../../../../common/state/KVCacheManager';
import {useGetTraceServerClientContext} from '../../../wfReactInterface/traceServerClientContext';

/**
 * Output Caching Manager
 *
 * High-level purpose
 * - Provides a small adapter around a generic KV cache to lazily fetch and cache
 *   call outputs from the Trace Server as cells come into view.
 * - Exposes a minimal API tailored for table-like UIs:
 *   - eventObserver.subscribe(listener): consumers subscribe to batched load
 *     events for individual call IDs.
 *   - requestCallOutput(callId): enqueue a load request for a call's output.
 *   - reportAway(callId): hint to evict when a cell scrolls out of view.
 *
 * Behavior and design
 * - Batching: requests are coalesced (batchSize, debounceMs) to reduce server requests.
 * - Loading: uses callsStreamQuery with columns ['id','output'] and expands
 *   the 'output' column for direct consumption in cells.
 * - Events: normalizes the underlying KV events to OutputLoadEvent so callers
 *   can render loading spinners, values, or errors.
 * - Eviction: reportAway delegates to the KV cache eviction, allowing upstream
 *   cache policy (e.g., LRU) to reclaim memory when cells disappear.
 *
 * Typical usage
 * - A cell component subscribes once in an effect, calls requestCallOutput when
 *   it mounts or becomes visible, renders based on event status/value, and calls
 *   reportAway when scrolling out of view.
 */

export type OutputLoadEvent = {
  callId: string;
  status: 'loading' | 'loaded' | 'error' | 'evicted';
  data?: any;
  error?: any;
};

export type OutputEventObserver = {
  subscribe: (listener: (evt: OutputLoadEvent) => void) => () => void;
};

export type OutputCachingManager = {
  eventObserver: OutputEventObserver;
  requestCallOutput: (callId: string) => void;
  purgeCache: () => void;
};

type CallRow = {id: string; output: any};

export const useOutputCachingManager = (
  projectId: string
): OutputCachingManager => {
  const getTsClient = useGetTraceServerClientContext();

  const kvOptions = useMemo(
    () => ({
      batchLoad: async (ids: readonly string[]) => {
        const res = await getTsClient().callsStreamQuery({
          project_id: projectId,
          filter: {call_ids: ids as string[]},
          columns: ['id', 'output'],
          expand_columns: ['output'],
          sort_by: [],
          limit: Math.max(1000, ids.length),
        });
        const calls = (res.calls ?? []) as any[];
        return calls.map(c => ({id: c.id as string, output: c.output ?? null}));
      },
      getId: (row: CallRow) => row.id,
      getValue: (row: CallRow) => row.output,
      batchSize: 500,
      debounceMs: 10,
    }),
    [getTsClient, projectId]
  );

  const kv = useKVCacheManager<string, CallRow, any>(kvOptions);

  const eventObserver: OutputEventObserver = useMemo(() => {
    return {
      subscribe: (listener: (evt: OutputLoadEvent) => void) =>
        kv.eventObserver.subscribe((e: KVLoadEvent<string, any>) =>
          listener({
            callId: e.id,
            status: e.status === 'idle' ? 'loading' : e.status,
            data: e.value,
            error: e.error,
          })
        ),
    };
  }, [kv.eventObserver]);

  return useMemo(
    () => ({
      eventObserver,
      requestCallOutput: (callId: string) => kv.request(callId),
      purgeCache: () => kv.purgeCache(),
    }),
    [eventObserver, kv]
  );
};
